import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTriviaResultsAdmin } from '../hooks/useTriviaAdmin';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
`;

const Input = styled.input`
  padding: 0.65rem 0.8rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  &:focus {
    outline: 2px solid var(--primary);
    border-color: var(--primary);
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  tbody tr:hover {
    background: rgba(255, 255, 255, 0.04);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const BadgeUser = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
`;

const BadgeAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  object-fit: cover;
`;

const BadgeInitials = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: var(--outline);
  color: var(--onSurfaceVariant);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
`;

const EmptyText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0;
`;

export const TriviaResultsPage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const filters = useMemo(
    () => ({
      title,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    [title, startDate, endDate]
  );

  const { data = [], isLoading, error } = useTriviaResultsAdmin(filters);

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Past Quiz Results</HeaderTitle>
      </HeaderRow>

      <Card>
        <FilterGrid>
          <FieldRow>
            <Label htmlFor="quiz-title-filter">Quiz Title</Label>
            <Input
              id="quiz-title-filter"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Search by quiz title"
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="quiz-start-date">Start Date</Label>
            <Input
              id="quiz-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="quiz-end-date">End Date</Label>
            <Input
              id="quiz-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FieldRow>
        </FilterGrid>
      </Card>

      <Card>
        {isLoading ? (
          <EmptyText>Loading results…</EmptyText>
        ) : error ? (
          <EmptyText>Failed to load results.</EmptyText>
        ) : data.length === 0 ? (
          <EmptyText>No results found.</EmptyText>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Quiz</th>
                <th>Date</th>
                <th>User</th>
                <th>Badge</th>
                <th>Score</th>
                <th>Shared</th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.quiz_title}</td>
                  <td>
                    {entry.quiz_created_at
                      ? new Date(entry.quiz_created_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>
                    <BadgeUser>
                      {entry.user_avatar ? (
                        <BadgeAvatar src={entry.user_avatar} alt={entry.user_name} />
                      ) : (
                        <BadgeInitials>{entry.user_name.substring(0, 2).toUpperCase()}</BadgeInitials>
                      )}
                      {entry.user_name}
                    </BadgeUser>
                  </td>
                  <td>{entry.badge}</td>
                  <td>{entry.score}</td>
                  <td>{entry.share_to_badge_list ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </Page>
  );
};
